<template>
  <AppModal :visible="visible === true" :title="modalTitle" boxClass="max-w-4xl w-11/12" @close="handleClose">
    <div v-if="visible === true" class="p-4 space-y-6">
      <div class="diagram-wrapper">
        <div v-if="isReasoningLoading === true" class="flex justify-center items-center py-10">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
        <div v-if="reasoningError !== ''" class="text-center text-error font-medium py-6">
          {{ reasoningError }}
        </div>
        <div v-if="displayReasoningData !== null" class="diagram-column">
          <div class="diagram-node" :class="{ 'diagram-node-rtl': isRTL === true }">
            <div class="diagram-node-title" :class="{ 'diagram-node-title-rtl': isRTL === true }">{{ t('reports.reasoningModal.userMessageLabel', 'User Message') }}</div>
            <div class="diagram-node-text">{{ displayReasoningData.userMessage }}</div>
          </div>
          <div class="diagram-arrow">
            <Icon icon="tabler:arrow-down" class="text-primary" width="24" height="24" />
          </div>
          <div class="diagram-node" :class="{ 'diagram-node-rtl': isRTL === true }">
            <div class="diagram-node-title" :class="{ 'diagram-node-title-rtl': isRTL === true }">{{ t('reports.reasoningModal.understoodQuestionLabel', 'Understood Question') }}</div>
            <div v-if="displayReasoningData.showUnderstoodQuestionText === true" class="diagram-node-text">{{ displayReasoningData.understoodQuestion }}</div>
            <div v-if="Array.isArray(displayReasoningData.knowledgeQueries) === true && displayReasoningData.knowledgeQueries.length > 0" class="knowledge-queries-container">
              <div class="knowledge-queries-list">
                <span
                  v-for="(query, index) in displayReasoningData.knowledgeQueries"
                  :key="index"
                  class="knowledge-query-chip"
                >
                  {{ query }}
                </span>
              </div>
            </div>
          </div>
          <div class="diagram-arrow">
            <Icon icon="tabler:arrow-down" class="text-primary" width="24" height="24" />
          </div>
          <div class="diagram-node" :class="{ 'diagram-node-rtl': isRTL === true }">
            <div class="diagram-node-title" :class="{ 'diagram-node-title-rtl': isRTL === true }">{{ t('reports.sourcesModal.title') }}</div>
            <div v-if="displayReasoningData.knowledgeSources.length === 0" class="diagram-node-text">{{ t('reports.sourcesModal.emptyState') }}</div>
            <div v-else class="diagram-sources-container">
              <div v-for="(source, index) in displayReasoningData.knowledgeSources" :key="index" class="diagram-source-card">
                <div class="diagram-source-header">
                  <Icon :icon="source.type === 'website' ? 'tabler:world' : 'tabler:file'" class="diagram-source-icon" width="18" height="18" />
                  <span class="diagram-source-name">{{ source.name || formatSourceLabel(source, index) }}</span>
                </div>
                <a v-if="source.type === 'website' && source.url" :href="source.url" target="_blank" rel="noopener noreferrer" class="diagram-source-url">
                  <Icon icon="tabler:external-link" width="14" height="14" />
                  <span>{{ source.url }}</span>
                </a>
              </div>
            </div>
          </div>
          <div class="diagram-arrow">
            <Icon icon="tabler:arrow-down" class="text-primary" width="24" height="24" />
          </div>
          <div class="diagram-node diagram-node-verification" :class="{ 'diagram-node-rtl': isRTL === true }">
            <div class="verification-header" :class="{ 'verification-header-rtl': isRTL === true }">
              <Icon icon="tabler:shield-check" class="verification-agent-icon" width="24" height="24" />
              <div
                class="diagram-node-title verification-title"
                :class="{ 'verification-title-rtl': isRTL === true }"
                :dir="isRTL === true ? 'rtl' : 'ltr'"
              >
                {{ t('reports.reasoningModal.verificationLabel', 'Verification') }} - {{ t('reports.reasoningModal.verificationAgentLabel', 'Verification Agent') }}
              </div>
            </div>
            <div class="verification-content">
              <div class="verification-row">
                <Icon
                  v-if="displayReasoningData.verification.approved === true"
                  icon="tabler:circle-check"
                  class="verification-status-icon text-success"
                  width="28"
                  height="28"
                />
                <Icon
                  v-else-if="displayReasoningData.verification.approved === false"
                  icon="tabler:circle-x"
                  class="verification-status-icon text-error"
                  width="28"
                  height="28"
                />
                <Icon
                  v-else
                  icon="tabler:circle-dotted"
                  class="verification-status-icon text-neutral"
                  width="28"
                  height="28"
                />
                <div class="verification-status-text">
                  <div class="verification-status-label">
                    <template v-if="displayReasoningData.verification.approved === true">
                      {{ t('reports.boolean.true') }}
                    </template>
                    <template v-else-if="displayReasoningData.verification.approved === false">
                      {{ t('reports.boolean.false') }}
                    </template>
                    <template v-else>
                      {{ t('reports.notAvailable') }}
                    </template>
                  </div>
                  <div v-if="displayReasoningData.verification.attempted === true" class="verification-attempted">
                    {{ t('reports.reasoningModal.verificationAttempted', 'Verification attempted') }}
                  </div>
                </div>
              </div>
              <div v-if="displayReasoningData.verification.finalMessage !== ''" class="verification-message">
                {{ displayReasoningData.verification.finalMessage }}
              </div>
            </div>
          </div>
          <div class="diagram-arrow">
            <Icon icon="tabler:arrow-down" class="text-primary" width="24" height="24" />
          </div>
          <div class="diagram-node" :class="{ 'diagram-node-rtl': isRTL === true }">
            <div class="diagram-node-title" :class="{ 'diagram-node-title-rtl': isRTL === true }">{{ t('reports.reasoningModal.finalAnswerLabel', 'Final Answer') }}</div>
            <div class="diagram-node-text">{{ displayReasoningData.finalAnswer }}</div>
            <div v-if="displayReasoningData.suggestedResponses !== undefined && Array.isArray(displayReasoningData.suggestedResponses) === true && displayReasoningData.suggestedResponses.length > 0" class="suggested-responses-container">
              <div class="suggested-responses-title">{{ t('reports.reasoningModal.suggestedResponsesLabel', 'Suggested Responses') }}</div>
              <div class="suggested-responses-buttons">
                <button
                  v-for="(suggestion, index) in displayReasoningData.suggestedResponses"
                  :key="index"
                  class="suggested-response-button"
                  disabled
                >
                  {{ suggestion }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useI18n } from '@/i18n';
import { globalState } from '@/globalState';
import AppModal from './AppModal.vue';
import router from '@/router';
import { Icon } from '@iconify/vue/dist/offline';

const props = defineProps({
  visible: { type: Boolean, default: false },
  sessionId: { type: String, default: '' },
  messageId: { type: String, default: '' },
  forceDiagram: { type: Boolean, default: false }
});

const emit = defineEmits(['close']);

const toast = useToast();
const { t, currentLanguage } = useI18n();

const isReasoningLoading = ref(false);
const reasoningData = ref(null);
const reasoningError = ref('');

const modalTitle = computed(() => t('reports.reasoningModal.title'));

const isRTL = computed(() => {
  const lang = (currentLanguage?.value !== undefined ? currentLanguage.value : '').toString().toLowerCase();
  return lang === 'he' || lang === 'ar' || (typeof lang === 'string' && (lang.startsWith('he') === true || lang.startsWith('ar') === true));
});


const formatSourceLabel = (source, index) => {
  if (source.type === 'website') {
    return `${t('reports.sourcesModal.websiteSourceLabel')} ${index + 1}`;
  }
  if (source.type === 'file') {
    return `${t('reports.sourcesModal.fileSourceLabel')} ${index + 1}`;
  }
  return `${t('reports.sourcesModal.genericSourceLabel')} ${index + 1}`;
};

const getUniqueSources = (sources) => {
  const seen = new Set();
  const unique = [];
  for (const source of sources) {
    const key = source.url !== undefined && source.url !== null && source.url !== ''
      ? source.url.trim().toLowerCase()
      : (source.name !== undefined && source.name !== null && source.name !== ''
          ? source.name.trim().toLowerCase()
          : null);
    if (key !== null && seen.has(key) === false) {
      seen.add(key);
      unique.push(source);
    } else if (key === null) {
      unique.push(source);
    }
  }
  return unique;
};

const displayReasoningData = computed(() => {
  if (reasoningData.value !== null) {
    return reasoningData.value;
  }
  if (props.forceDiagram === true) {
    return {
      userMessage: '',
      understoodQuestion: '',
      knowledgeQueries: [],
      knowledgeSources: [],
      verification: {
        attempted: false,
        approved: null,
        finalMessage: '',
        steps: []
      },
      finalAnswer: '',
      suggestedResponses: [],
      showUnderstoodQuestionText: true
    };
  }
  return null;
});

const loadReasoning = async () => {
  if (props.visible !== true) {
    return;
  }
  const sessionId = typeof props.sessionId === 'string' ? props.sessionId.trim() : '';
  const messageId = typeof props.messageId === 'string' ? props.messageId.trim() : '';
  if (sessionId.length === 0) {
    reasoningError.value = t('reports.reasoningModal.invalidSession');
    reasoningData.value = null;
    return;
  }
  if (messageId.length === 0) {
    reasoningError.value = t('reports.sourcesModal.invalidMessage');
    reasoningData.value = null;
    return;
  }
  isReasoningLoading.value = true;
  reasoningError.value = '';
  reasoningData.value = null;
  try {
    const endpoint = `${globalState.apiEndpoint}/api/reports/conversation/${encodeURIComponent(sessionId)}/message/${encodeURIComponent(messageId)}/sources`;
    const response = await axios.get(endpoint, { withCredentials: true });
    if (response.data === undefined || response.data === null) {
      reasoningError.value = t('reports.reasoningModal.loadFailed');
      return;
    }
    const reasoning = response.data.reasoning || {};
    let knowledgeSources = Array.isArray(reasoning.knowledgeSources) === true ? reasoning.knowledgeSources : [];
    if (knowledgeSources.length === 0 && Array.isArray(response.data.sources) === true && response.data.sources.length > 0) {
      knowledgeSources = response.data.sources.map((source) => ({
        type: source.type,
        name: source.name,
        url: source.url,
        page: source.page,
        highlight: source.highlight
      }));
    }
    knowledgeSources = knowledgeSources.map((source) => ({
      type: source.type,
      name: source.name,
      url: source.url,
      page: source.page,
      highlight: typeof source.highlight === 'string' ? source.highlight : ''
    }));
    knowledgeSources = getUniqueSources(knowledgeSources);
    const knowledgeQueries = Array.isArray(reasoning.knowledgeBaseQueries) === true ? reasoning.knowledgeBaseQueries : [];
    const primaryKnowledgeQuery = knowledgeQueries.length > 0 ? knowledgeQueries[0] : '';
    const understoodQuestion =
      primaryKnowledgeQuery !== ''
        ? primaryKnowledgeQuery
        : (typeof reasoning.understoodQuestion === 'string' && reasoning.understoodQuestion.trim().length > 0
            ? reasoning.understoodQuestion
            : response.data.question);
    const showUnderstoodQuestionText = primaryKnowledgeQuery === '';
    const suggestedResponses = Array.isArray(response.data.suggestedResponses) === true ? response.data.suggestedResponses : [];
    reasoningData.value = {
      userMessage: typeof reasoning.userMessage === 'string' ? reasoning.userMessage : response.data.question,
      understoodQuestion,
      knowledgeQueries,
      knowledgeSources,
      verification: typeof reasoning.verification === 'object' && reasoning.verification !== null ? reasoning.verification : {
        attempted: false,
        approved: null,
        finalMessage: '',
        steps: []
      },
      finalAnswer: typeof reasoning.finalAnswer === 'string' ? reasoning.finalAnswer : response.data.answer,
      suggestedResponses,
      showUnderstoodQuestionText
    };
  } catch (error) {
    if (error.response !== undefined && (error.response.status === 401 || error.response.status === 403)) {
      emit('close');
      router.push('/login');
      return;
    }
    if (error.response !== undefined && error.response.status === 404) {
      reasoningError.value = t('reports.reasoningModal.notFound');
    } else {
      reasoningError.value = t('reports.reasoningModal.loadFailed');
      toast.error(t('reports.reasoningModal.loadFailed'));
    }
    console.error('Error loading reasoning data:', error);
  } finally {
    isReasoningLoading.value = false;
  }
};

const resetState = () => {
  isReasoningLoading.value = false;
  reasoningData.value = null;
  reasoningError.value = '';
};

const handleClose = () => {
  emit('close');
};

watch(
  () => props.visible,
  (newValue, oldValue) => {
    if (newValue === true) {
      loadReasoning();
    } else if (oldValue === true) {
      resetState();
    }
  }
);

watch(
  () => [props.sessionId, props.messageId],
  (newValues, oldValues) => {
    if (props.visible === true && (oldValues === undefined || newValues[0] !== oldValues[0] || newValues[1] !== oldValues[1])) {
      loadReasoning();
    }
  }
);
</script>

<style scoped>
.diagram-wrapper {
  border-radius: 0.75rem;
  border: 1px solid var(--fallback-b2,rgba(148,163,184,0.35));
  padding: 1.25rem;
}
.diagram-column {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.diagram-node {
  width: 100%;
  max-width: 26rem;
  background-color: var(--fallback-b1,rgba(30,41,59,0.05));
  border: 1px solid var(--fallback-b2,rgba(148,163,184,0.4));
  border-radius: 0.75rem;
  padding: 1rem;
  
  
}
.diagram-node-rtl {
  direction: rtl;
  text-align: right;
}
.diagram-node + .diagram-node {
  margin-top: 0;
}
.diagram-node-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
  direction: ltr;
  text-align: left;
}
.diagram-node-title-rtl {
  direction: rtl;
  text-align: right;
}
.diagram-node-text {
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-word;
}
.diagram-node-verification {
  background-color: var(--fallback-b1,rgba(59,130,246,0.1));
  border: 2px solid var(--fallback-p,#3b82f6);
  box-shadow: 0 4px 6px -1px rgba(59,130,246,0.2);
  padding: 1.25rem;
}
.verification-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  direction: ltr;
  justify-content: flex-start;
  width: 100%;
}
.verification-header-rtl {
  direction: rtl;
}
.verification-agent-icon {
  color: var(--fallback-p,#3b82f6);
  margin-right: 0.5rem;
  flex-shrink: 0;
}
.verification-header-rtl .verification-agent-icon {
  margin-right: 0;
  margin-left: 0.5rem;
}
.verification-title {
  margin-bottom: 0;
  direction: ltr;
  text-align: left;
}
.verification-title-rtl {
  direction: rtl;
  text-align: right;
  unicode-bidi: bidi-override;
}
.verification-content {
  padding-top: 0.5rem;
}
.verification-row {
  display: flex;
  align-items: center;
  direction: ltr;
  padding: 0.75rem;
  background-color: var(--fallback-b1,rgba(255,255,255,0.5));
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
}
.diagram-node-rtl .verification-row {
  flex-direction: row-reverse;
  direction: rtl;
}
.verification-status-icon {
  flex-shrink: 0;
  margin-right: 0.75rem;
}
.diagram-node-rtl .verification-status-icon {
  margin-right: 0;
  margin-left: 0.75rem;
}
.verification-status-text {
  flex: 1;
  direction: ltr;
}
.diagram-node-rtl .verification-status-text {
  direction: rtl;
  text-align: right;
}
.verification-status-label {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.25rem;
}
.verification-attempted {
  font-size: 0.85rem;
  color: var(--fallback-b4,rgba(148,163,184,1));
}
.verification-message {
  padding: 0.75rem;
  background-color: var(--fallback-b1,rgba(255,255,255,0.5));
  border-radius: 0.5rem;
  font-size: 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
}
.diagram-node-subtext {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  white-space: pre-wrap;
}
.diagram-arrow {
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
}
.diagram-sources-container {
  display: flex;
  flex-direction: column;
  direction: ltr;
}
.diagram-node-rtl .diagram-sources-container {
  direction: rtl;
}
.diagram-source-card {
  background-color: var(--fallback-b1,rgba(255,255,255,0.6));
  border: 1px solid var(--fallback-b2,rgba(148,163,184,0.25));
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-top: 0.5rem;
  transition: all 0.2s ease;
  direction: ltr;
}
.diagram-source-card:first-of-type {
  margin-top: 0;
}
.diagram-source-card:hover {
  background-color: var(--fallback-b1,rgba(255,255,255,0.8));
  border-color: var(--fallback-b2,rgba(148,163,184,0.4));
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}
.diagram-node-rtl .diagram-source-card {
  direction: rtl;
}
.diagram-source-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  direction: ltr;
}
.diagram-node-rtl .diagram-source-header {
  flex-direction: row-reverse;
  direction: rtl;
}
.diagram-source-icon {
  color: var(--fallback-p,#3b82f6);
  margin-right: 0.5rem;
  flex-shrink: 0;
}
.diagram-node-rtl .diagram-source-icon {
  margin-right: 0;
  margin-left: 0.5rem;
}
.diagram-source-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--fallback-bc,rgba(30,41,59,0.9));
  word-break: break-word;
}
.diagram-source-url {
  display: inline-flex;
  align-items: center;
  color: var(--fallback-p,#2563eb);
  text-decoration: none;
  font-size: 0.85rem;
  word-break: break-all;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  background-color: var(--fallback-b1,rgba(59,130,246,0.1));
  transition: all 0.2s ease;
  direction: ltr;
  margin-top: 0.25rem;
}
.diagram-source-url:hover {
  background-color: var(--fallback-b1,rgba(59,130,246,0.15));
  text-decoration: underline;
}
.diagram-source-url svg {
  margin-right: 0.25rem;
  flex-shrink: 0;
}
.diagram-node-rtl .diagram-source-url {
  direction: rtl;
  flex-direction: row-reverse;
}
.diagram-node-rtl .diagram-source-url svg {
  margin-right: 0;
  margin-left: 0.25rem;
}
.knowledge-queries-container {
  margin-top: 1rem;
}
.knowledge-queries-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: var(--fallback-b4,rgba(148,163,184,1));
}
.knowledge-queries-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  direction: ltr;
}
.diagram-node-rtl .knowledge-queries-list {
  direction: rtl;
  justify-content: flex-end;
}
.knowledge-query-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 0.5rem;
  background-color: var(--fallback-b1,rgba(148,163,184,0.12));
  border: 1px solid var(--fallback-b2,rgba(148,163,184,0.3));
  font-size: 0.85rem;
  color: var(--fallback-bc,rgba(30,41,59,0.85));
  white-space: nowrap;
}
.suggested-responses-container {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--fallback-b2,rgba(148,163,184,0.3));
}
.suggested-responses-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  color: var(--fallback-b4,rgba(148,163,184,1));
}
.suggested-responses-buttons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  direction: ltr;
}
.diagram-node-rtl .suggested-responses-buttons {
  direction: rtl;
  justify-content: flex-end;
  flex-direction: row-reverse;
}
.suggested-response-button {
  padding: 0.5rem 1rem;
  margin: 0.25rem;
  background-color: var(--fallback-b1,rgba(148,163,184,0.1));
  border: 1px solid var(--fallback-b2,rgba(148,163,184,0.3));
  border-radius: 0.5rem;
  font-size: 0.9rem;
  cursor: not-allowed;
  opacity: 0.7;
  color: var(--fallback-bc,rgba(30,41,59,0.8));
  white-space: nowrap;
  transition: background-color 0.2s;
}
.suggested-response-button:hover {
  background-color: var(--fallback-b1,rgba(148,163,184,0.15));
}
</style>
